import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  ShoppingCart,
  Package,
  ReceiptText,
  Boxes,
  RefreshCw,
  TrendingUp,
  CheckCircle2,
  X,
} from 'lucide-react';
import { Button } from '../ui/index.js';
import { cn } from '../ui/cn.js';
import {
  fetchSalesStats,
  fetchSalesStatsBrands,
  fetchSalesStatsCategories,
  fetchSalesStatuses,
  saveCountedStatuses,
  fetchReconcileTotalSales,
} from '../api.js';

function formatTnd(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return value.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) + ' TND';
}

/**
 * S21 — Sales Stats (purchase manager): units + revenue per product from the
 * completed WooCommerce orders, filterable by brand family (brand + its
 * sub-brands) and category, over a selectable period.
 */
export function SalesStatsPage(): React.ReactElement {
  const [brand, setBrand] = useState<string>('all');
  const [category, setCategory] = useState<string>('all');
  const [periodMode, setPeriodMode] = useState<'preset' | 'custom'>('preset');
  const [days, setDays] = useState<number>(90);
  // custom range: history starts 2026-03-01 (store launch)
  const [fromDate, setFromDate] = useState<string>('2023-03-01');
  const [toDate, setToDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [sort, setSort] = useState<'qty' | 'revenue'>('qty');
  const [horizon, setHorizon] = useState<1 | 2 | 3>(2);
  const [showReconciliation, setShowReconciliation] = useState<boolean>(false);



  const reconcileQuery = useQuery({
    queryKey: ['sales-reconcile-total-sales'],
    queryFn: () => fetchReconcileTotalSales(50),
    enabled: showReconciliation,
    staleTime: 60_000,
  });

  const brandsQuery = useQuery({ queryKey: ['sales-brands'], queryFn: fetchSalesStatsBrands });
  const categoriesQuery = useQuery({
    queryKey: ['sales-categories'],
    queryFn: fetchSalesStatsCategories,
  });
  const statsQuery = useQuery({
    queryKey: ['sales-stats', brand, category, periodMode, days, fromDate, toDate, sort],
    queryFn: () =>
      fetchSalesStats({
        brand,
        category,
        days,
        sort,
        from: periodMode === 'custom' ? fromDate : undefined,
        to: periodMode === 'custom' ? toDate : undefined,
      }),
    refetchInterval: 5 * 60_000,
  });

  // counted-statuses editor (client rule: Terminée + En cours de preparation —
  // editable here for when the status list gets cleaned up)
  const queryClient = useQueryClient();
  const statusesQuery = useQuery({
    queryKey: ['sales-statuses'],
    queryFn: fetchSalesStatuses,
  });
  const [countedDraft, setCountedDraft] = useState<readonly string[] | null>(null);
  const countedCurrent = countedDraft ?? statusesQuery.data?.counted ?? [];
  const saveCounted = useMutation({
    mutationFn: saveCountedStatuses,
    onSuccess: () => {
      setCountedDraft(null);
      void queryClient.invalidateQueries({ queryKey: ['sales-statuses'] });
      void queryClient.invalidateQueries({ queryKey: ['sales-stats'] });
    },
  });
  const toggleCounted = (status: string): void => {
    setCountedDraft(
      countedCurrent.includes(status)
        ? countedCurrent.filter((s) => s !== status)
        : [...countedCurrent, status],
    );
  };

  const stats = statsQuery.data;
  const brandFamily = stats?.brandFamily ?? null;
  const rows = stats?.rows ?? [];

  const exportCsv = (): void => {
    const header = [
      'SKU',
      'Produit',
      'Marque',
      'Unités vendues',
      'CA TTC',
      'Commandes',
      'Vente/mois',
      `Prévision 1 mois`,
      `Prévision 2 mois`,
      `Prévision 3 mois`,
      'Stock site',
      'Couverture (jours)',
      `À commander (${horizon} mois)`,
    ].join(';');
    const lines = rows.map((r) =>
      [
        r.sku,
        `"${r.name.replace(/"/g, '""')}"`,
        r.brand ?? '',
        r.qtySold,
        r.revenueTtc,
        r.ordersCount,
        r.monthlyRate,
        r.forecast1,
        r.forecast2,
        r.forecast3,
        r.stockQty ?? '',
        r.coverageDays ?? '',
        horizon === 1 ? r.order1 : horizon === 2 ? r.order2 : r.order3,
      ].join(';'),
    );
    const blob = new Blob(['\uFEFF' + [header, ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stats-ventes-${brand === 'all' ? 'toutes-marques' : brand}-${days}j.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5 pb-16">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-amber-100 border border-amber-600/20 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-amber-800">
            S21 · Grossistes B2B
          </span>
        </div>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-ink font-display">Stats Ventes</h1>
        <p className="text-sm text-ink-soft">
          Unités vendues et chiffre d&apos;affaires par produit, marque (avec ses
          sous-marques) et catégorie, depuis les commandes terminées et en cours
          de préparation (completed + en-cours-de-prepa) du site. Renouvelé toutes
          les 5 minutes.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card/80 p-4 shadow-xs">
        <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
          Marque (+ sous-marques)
          <select
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            className="h-9 min-w-52 rounded-lg border border-border bg-card px-2.5 text-xs font-semibold text-ink shadow-xs"
          >
            <option value="all">Toutes les marques</option>
            {brandsQuery.data?.brands.map((b) => (
              <option key={b.brandNorm} value={b.brand}>
                {b.brand}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
          Catégorie
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="h-9 min-w-48 rounded-lg border border-border bg-card px-2.5 text-xs font-semibold text-ink shadow-xs"
          >
            <option value="all">Toutes les catégories</option>
            {categoriesQuery.data?.categories.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name} ({c.count})
              </option>
            ))}
          </select>
        </label>
        <div className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
          <span>Période</span>
          <div className="flex overflow-hidden rounded-lg border border-border">
            <button
              type="button"
              onClick={() => setPeriodMode('preset')}
              className={cn(
                'px-3 py-1.5 font-semibold transition-colors',
                periodMode === 'preset' ? 'bg-herb-600 text-white' : 'bg-card text-ink-soft hover:bg-muted',
              )}
            >
              Durées
            </button>
            <button
              type="button"
              onClick={() => setPeriodMode('custom')}
              className={cn(
                'px-3 py-1.5 font-semibold transition-colors',
                periodMode === 'custom' ? 'bg-herb-600 text-white' : 'bg-card text-ink-soft hover:bg-muted',
              )}
            >
              Dates
            </button>
          </div>
        </div>
        {periodMode === 'preset' ? (
          <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
            Durée
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="h-9 rounded-lg border border-border bg-card px-2.5 text-xs font-semibold text-ink shadow-xs"
            >
              <option value={30}>30 derniers jours</option>
              <option value={90}>90 derniers jours</option>
              <option value={180}>180 derniers jours</option>
              <option value={365}>12 derniers mois</option>
            </select>
          </label>
        ) : (
          <>
            <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
              Du
              <input
                type="date"
                value={fromDate}
                min="2023-03-01"
                onChange={(e) => setFromDate(e.target.value)}
                className="h-9 rounded-lg border border-border bg-card px-2.5 text-xs font-semibold text-ink shadow-xs"
              />
            </label>
            <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
              Au
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="h-9 rounded-lg border border-border bg-card px-2.5 text-xs font-semibold text-ink shadow-xs"
              />
            </label>
          </>
        )}
        <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
          Tri
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value === 'revenue' ? 'revenue' : 'qty')}
            className="h-9 rounded-lg border border-border bg-card px-2.5 text-xs font-semibold text-ink shadow-xs"
          >
            <option value="qty">Par unités vendues</option>
            <option value="revenue">Par chiffre d&apos;affaires</option>
          </select>
        </label>
        <span className="flex-1" />
        <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
          Horizon de commande
          <select
            value={horizon}
            onChange={(e) => setHorizon(Number(e.target.value) === 3 ? 3 : Number(e.target.value) === 2 ? 2 : 1)}
            className="h-9 rounded-lg border border-border bg-card px-2.5 text-xs font-semibold text-ink shadow-xs"
          >
            <option value={1}>Couvrir 1 mois</option>
            <option value={2}>Couvrir 2 mois</option>
            <option value={3}>Couvrir 3 mois</option>
          </select>
        </label>
        <Button variant="outline" size="sm" disabled={rows.length === 0} onClick={exportCsv}>
          Exporter CSV
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={statsQuery.isFetching}
          onClick={() => void statsQuery.refetch()}
        >
          <RefreshCw className={cn('h-4 w-4', statsQuery.isFetching && 'animate-spin')} />
          Actualiser
        </Button>
        <Button
          variant={showReconciliation ? 'primary' : 'outline'}
          size="sm"
          disabled={reconcileQuery.isFetching}
          onClick={() => setShowReconciliation((prev) => !prev)}
        >
          <CheckCircle2 className={cn('h-4 w-4', reconcileQuery.isFetching && 'animate-spin')} />
          Vérifier vs total_sales Woo
        </Button>
      </div>

      {/* Fix C: Reconciliation report banner */}
      {showReconciliation && (
        <div className="rounded-xl border border-border bg-card p-5 shadow-xs space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-herb-100 border border-herb-600/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-herb-800">
                  Fix C · Audit Intégrité
                </span>
                <h2 className="text-base font-bold text-ink font-display">
                  Réconciliation vs total_sales WooCommerce
                </h2>
              </div>
              <p className="mt-1 text-xs text-ink-soft">
                Comparaison entre la quantité à vie calculée depuis l&apos;historique de nos commandes ({reconcileQuery.data?.summary.countedStatuses.join(', ') || 'completed, en-cours-de-prepa'}) et le compteur officiel total_sales renvoyé par WooCommerce.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="xs"
                disabled={reconcileQuery.isFetching}
                onClick={() => void reconcileQuery.refetch()}
              >
                <RefreshCw className={cn('h-3.5 w-3.5', reconcileQuery.isFetching && 'animate-spin')} />
                Re-vérifier
              </Button>
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setShowReconciliation(false)}
              >
                <X className="h-4 w-4" />
                Fermer
              </Button>
            </div>
          </div>

          {reconcileQuery.isLoading ? (
            <div className="flex items-center justify-center p-8 text-xs text-ink-soft">
              <RefreshCw className="mr-2 h-4 w-4 animate-spin text-herb-600" />
              Calcul de l&apos;historique des commandes et réconciliation en cours…
            </div>
          ) : reconcileQuery.isError ? (
            <div className="p-4 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-700">
              Erreur lors du calcul de réconciliation : {String(reconcileQuery.error)}
            </div>
          ) : reconcileQuery.data ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-lg border border-border/80 bg-muted/30 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-ink-faint">Produits analysés</p>
                  <p className="text-xl font-bold text-ink mt-0.5">{reconcileQuery.data.summary.totalProducts.toLocaleString('fr-FR')}</p>
                  <p className="text-[11px] text-ink-ghost mt-0.5">au catalogue miroir</p>
                </div>
                <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Concordants (Delta 0)</p>
                  <p className="text-xl font-bold text-emerald-800 mt-0.5">{reconcileQuery.data.summary.matchingCount.toLocaleString('fr-FR')}</p>
                  <p className="text-[11px] text-emerald-600 mt-0.5">ventes calculées = Woo</p>
                </div>
                <div className={cn(
                  "rounded-lg border p-3",
                  reconcileQuery.data.summary.discrepancyCount > 0
                    ? "border-amber-500/30 bg-amber-500/5 text-amber-900"
                    : "border-border/80 bg-muted/30 text-ink"
                )}>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">En écart</p>
                  <p className="text-xl font-bold mt-0.5">{reconcileQuery.data.summary.discrepancyCount.toLocaleString('fr-FR')}</p>
                  <p className="text-[11px] text-amber-600 mt-0.5">différence constatée</p>
                </div>
                <div className="rounded-lg border border-border/80 bg-muted/30 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-ink-faint">Sans compteur Woo</p>
                  <p className="text-xl font-bold text-ink mt-0.5">{reconcileQuery.data.summary.noCounterCount.toLocaleString('fr-FR')}</p>
                  <p className="text-[11px] text-ink-ghost mt-0.5">en attente de synchro</p>
                </div>
              </div>

              {/* Volume stats */}
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-ink-soft bg-muted/40 rounded-lg px-3 py-2 border border-border/60">
                <span>
                  Total unités calculées (commandes) : <strong className="text-ink">{reconcileQuery.data.summary.totalCalculatedUnits.toLocaleString('fr-FR')}</strong>
                </span>
                <span>
                  Total unités déclarées (WooCommerce) : <strong className="text-ink">{reconcileQuery.data.summary.totalWooUnits.toLocaleString('fr-FR')}</strong>
                </span>
                <span>
                  Écart global : <strong className={cn(
                    reconcileQuery.data.summary.totalCalculatedUnits === reconcileQuery.data.summary.totalWooUnits
                      ? "text-emerald-700"
                      : "text-amber-700"
                  )}>
                    {reconcileQuery.data.summary.totalCalculatedUnits - reconcileQuery.data.summary.totalWooUnits > 0 ? '+' : ''}
                    {(reconcileQuery.data.summary.totalCalculatedUnits - reconcileQuery.data.summary.totalWooUnits).toLocaleString('fr-FR')}
                  </strong>
                </span>
              </div>

              {/* Top discrepancies table */}
              {reconcileQuery.data.topDiscrepancies.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-ink uppercase tracking-wider">
                      Top {reconcileQuery.data.topDiscrepancies.length} écarts par volume absolu
                    </p>
                    <span className="text-[11px] text-ink-ghost">
                      Delta &gt; 0 = plus dans nos commandes · Delta &lt; 0 = plus dans total_sales Woo
                    </span>
                  </div>
                  <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-muted/60 text-[10px] font-bold uppercase tracking-wider text-ink-soft">
                        <tr>
                          <th className="px-3 py-2">SKU</th>
                          <th className="px-3 py-2">Produit</th>
                          <th className="px-3 py-2 text-right">Qté Commandes</th>
                          <th className="px-3 py-2 text-right">total_sales Woo</th>
                          <th className="px-3 py-2 text-right">Écart (Delta)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {reconcileQuery.data.topDiscrepancies.map((d) => (
                          <tr key={d.sku} className="hover:bg-muted/30">
                            <td className="px-3 py-2 font-mono font-medium text-ink-soft">{d.sku}</td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                {d.image && (
                                  <img src={d.image} alt="" className="h-6 w-6 rounded object-cover" />
                                )}
                                <div>
                                  <p className="font-medium text-ink truncate max-w-xs">{d.name}</p>
                                  {d.brand && <p className="text-[10px] text-ink-ghost">{d.brand}</p>}
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right font-mono font-semibold text-ink">
                              {d.calculatedQty}
                            </td>
                            <td className="px-3 py-2 text-right font-mono font-semibold text-ink">
                              {d.totalSalesWoo}
                            </td>
                            <td className="px-3 py-2 text-right font-mono font-bold">
                              <span className={cn(
                                "inline-block px-1.5 py-0.5 rounded text-[11px]",
                                d.delta > 0
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-amber-100 text-amber-800"
                              )}>
                                {d.delta > 0 ? `+${d.delta}` : d.delta}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="p-3 text-center text-xs text-emerald-700 bg-emerald-50 rounded-lg border border-emerald-200">
                  ✅ Parfaite concordance ! Tous les produits vérifiés présentent un delta nul avec le compteur WooCommerce.
                </div>
              )}
            </>
          ) : null}
        </div>
      )}

      {/* Counted statuses — sales definition editor */}
      <div className="rounded-xl border border-border bg-card/80 p-4 shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-ink-soft">
              Statuts comptés comme ventes
            </p>
            <p className="mt-0.5 text-[11px] text-ink-ghost">
              Règle actuelle : Terminée + En cours de préparation. Cochez/décochez
              pour changer — utile après le nettoyage des statuts.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {countedDraft !== null && (
              <Button type="button" variant="ghost" size="xs" onClick={() => setCountedDraft(null)}>
                Annuler
              </Button>
            )}
            <Button
              type="button"
              variant="primary"
              size="xs"
              disabled={
                saveCounted.isPending ||
                countedDraft === null ||
                countedCurrent.length === 0
              }
              onClick={() => saveCounted.mutate([...countedCurrent])}
            >
              {saveCounted.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        </div>
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {(statusesQuery.data?.statuses ?? []).map((s) => {
            const counted = countedCurrent.includes(s.status);
            return (
              <button
                key={s.status}
                type="button"
                onClick={() => toggleCounted(s.status)}
                title={`${s.orders.toLocaleString('fr-FR')} commande(s) dans le miroir`}
                className={cn(
                  'rounded-full border px-2.5 py-1 font-mono text-[11px] font-semibold transition-colors',
                  counted
                    ? 'border-herb-600 bg-herb-600 text-white'
                    : 'border-border bg-card text-ink-soft hover:border-border-strong',
                )}
              >
                {counted ? '✓ ' : ''}
                {s.status}
                <span className={cn('ml-1.5 font-sans', counted ? 'text-herb-100' : 'text-ink-ghost')}>
                  {s.orders.toLocaleString('fr-FR')}
                </span>
              </button>
            );
          })}
          {statusesQuery.data !== undefined && (statusesQuery.data.statuses.length === 0) && (
            <span className="text-[11px] text-ink-ghost">
              Aucune commande synchronisée — la découverte des statuts arrive avec le backfill.
            </span>
          )}
        </div>
        {saveCounted.isError && (
          <p className="mt-2 text-[11px] text-rose-700">Échec : {String(saveCounted.error.message)}</p>
        )}
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { label: 'Unités vendues', value: stats?.totals.units, icon: Boxes },
          { label: 'CA TTC vendu', value: stats === undefined ? undefined : stats.totals.revenue, icon: ReceiptText, money: true },
          { label: 'Commandes', value: stats?.totals.orders, icon: ShoppingCart },
          { label: 'Produits vendus', value: stats?.totals.products, icon: Package },
        ].map((k) => (
          <div key={k.label} className="rounded-xl border border-border bg-card p-3.5 shadow-xs">
            <div className="flex items-center justify-between text-ink-ghost">
              <span className="text-xs font-semibold uppercase tracking-wider">{k.label}</span>
              <k.icon className="h-4 w-4 text-slate-500" />
            </div>
            <div className="mt-2 text-2xl font-extrabold text-ink font-mono">
              {k.money === true
                ? formatTnd(k.value)
                : (k.value ?? 0).toLocaleString('fr-FR')}
            </div>
          </div>
        ))}
      </div>

      {brandFamily !== null && brandFamily.length > 1 && (
        <p className="text-xs text-ink-soft">
          Famille incluse : {brandFamily.join(', ')}
        </p>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-xs">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-border bg-cream/40 text-[10px] uppercase tracking-wider text-ink-faint">
              <th className="px-4 py-2.5 font-semibold">Produit</th>
              <th className="px-3 py-2.5 font-semibold">Marque</th>
              <th className="px-3 py-2.5 text-right font-semibold">Unités</th>
              <th className="px-3 py-2.5 text-right font-semibold">CA TTC</th>
              <th className="px-3 py-2.5 text-right font-semibold">Vente/mois</th>
              <th className="px-3 py-2.5 text-center font-semibold">Prév. 1 / 2 / 3 mois</th>
              <th className="px-3 py-2.5 text-right font-semibold">Stock site</th>
              <th className="px-3 py-2.5 text-right font-semibold">Couverture</th>
              <th className="px-3 py-2.5 text-right font-semibold">À commander ({horizon} mois)</th>
              <th className="px-3 py-2.5 text-right font-semibold">Dernière vente</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(stats?.rows ?? []).map((row) => {
              const suggested = horizon === 1 ? row.order1 : horizon === 2 ? row.order2 : row.order3;
              return (
              <tr key={row.sku} className="hover:bg-cream/30">
                <td className="max-w-md px-4 py-2.5">
                  <div className="flex items-center gap-2.5">
                    {row.image !== null && row.image !== '' && (
                      <img
                        src={row.image}
                        alt=""
                        className="h-10 w-10 shrink-0 rounded-lg border border-border object-cover bg-cream"
                      />
                    )}
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-ink">{row.name}</p>
                      <p className="font-mono text-[11px] text-ink-faint">{row.sku}</p>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-ink-soft">{row.brand ?? '—'}</td>
                <td className="px-3 py-2.5 text-right font-mono font-bold text-ink">{row.qtySold}</td>
                <td className="px-3 py-2.5 text-right font-mono text-ink">{formatTnd(row.revenueTtc)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-ink-soft">{row.monthlyRate}</td>
                <td className="px-3 py-2.5 text-center font-mono text-[11px] text-ink-soft">
                  {row.forecast1} / {row.forecast2} / {row.forecast3}
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-ink-soft">
                  {row.stockQty === null ? '—' : row.stockQty}
                </td>
                <td className="px-3 py-2.5 text-right font-mono">
                  {row.coverageDays === null ? (
                    <span className="text-ink-ghost">—</span>
                  ) : row.coverageDays > 90 ? (
                    <span className="text-emerald-700">{row.coverageDays} j</span>
                  ) : row.coverageDays > 30 ? (
                    <span className="text-amber-700">{row.coverageDays} j</span>
                  ) : (
                    <span className="text-rose-700 font-bold">{row.coverageDays} j</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right">
                  <span
                    className={cn(
                      'inline-block rounded-md px-2 py-0.5 font-mono font-bold',
                      suggested === 0 ? 'bg-herb-50 text-herb-700' : 'bg-sky-50 text-sky-800 border border-sky-600/30',
                    )}
                  >
                    {suggested}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-[11px] text-ink-soft">
                  {row.lastSoldAt !== null
                    ? new Date(row.lastSoldAt).toLocaleDateString('fr-FR')
                    : '—'}
                </td>
              </tr>
              );
            })}
            {statsQuery.data !== undefined && rows.length === 0 && (
              <tr>
                <td colSpan={10} className="px-4 py-10 text-center text-sm text-ink-ghost">
                  <TrendingUp className="mx-auto mb-2 h-6 w-6 opacity-40" />
                  Aucune vente terminée sur cette période / ces filtres. Les commandes
                  « terminées » remontent du site toutes les 15 minutes — au premier
                  lancement après déploiement du worker, l&apos;historique complet se
                  télécharge en quelques minutes.
                </td>
              </tr>
            )}
            {statsQuery.isLoading && (
              <tr>
                <td colSpan={10} className="px-4 py-10 text-center text-sm text-ink-ghost">
                  Agrégation des ventes…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-ink-faint">
        Source : commandes WooCommerce au statut « terminé ». Besoin du détail
        d&apos;une vente ?{' '}
        <Link to="/shop-manager" className="text-herb-700 underline">
          Console boutique
        </Link>
      </p>
    </div>
  );
}
